import React, { useState } from 'react';

const todayIso = () => new Date().toLocaleDateString('en-CA');
const money = (n) => Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 });

/**
 * One dialog for all three bulk actions on selected challans.
 *
 * `markPaid` and `dueDate` are transactional server-side — the whole batch lands
 * or none of it does — so their result is a simple count. `reminder` is not, and
 * cannot be: a message that has left the building can't be recalled. That one
 * reports per-challan outcomes, which is why the result panel below renders a
 * list rather than a single line.
 *
 * Props:
 *  - action   'markPaid' | 'dueDate' | 'reminder'
 *  - rows     the selected ChallanPaymentRow objects
 *  - onClose(didApply)  didApply=false when the operator backed out, so the caller
 *                       can keep their selection instead of silently dropping it
 *  - onSubmit(payload) -> { success, data?: {requested, applied, skipped, failed, results}, error? }
 */
export default function BulkActionModal({ action, rows = [], onClose, onSubmit }) {
  const [paidOn, setPaidOn] = useState(todayIso());
  const [mode, setMode] = useState('bank');
  const [reference, setReference] = useState('');
  const [note, setNote] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [remark, setRemark] = useState('');
  const [email, setEmail] = useState(true);
  const [whatsapp, setWhatsapp] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);
  const [result, setResult] = useState(null);

  const ids = rows.map((r) => r.sub_order_id);
  // Each challan settles its OWN outstanding, so the figure shown is the sum of
  // what is actually owed — not a number the operator types and we divide up.
  const unpaid = rows.filter((r) => r.payment_status !== 'paid');
  const totalOutstanding = unpaid.reduce((sum, r) => sum + Number(r.outstanding || 0), 0);

  const titles = {
    markPaid: 'Mark as Fully Paid',
    dueDate: 'Change Due Date',
    reminder: 'Send Reminders',
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr(null);

    let payload;
    if (action === 'markPaid') {
      if (unpaid.length === 0) { setErr('Every selected challan is already paid.'); return; }
      payload = {
        sub_order_ids: ids,
        paid_on: paidOn || undefined,
        mode: mode || undefined,
        reference: reference || undefined,
        note: note || undefined,
      };
    } else if (action === 'dueDate') {
      if (!dueDate) { setErr('Pick a due date'); return; }
      if (!remark.trim()) { setErr('A remark is required'); return; }
      payload = { sub_order_ids: ids, due_date: dueDate, remark: remark.trim() };
    } else {
      const channels = [...(email ? ['email'] : []), ...(whatsapp ? ['whatsapp'] : [])];
      if (channels.length === 0) { setErr('Pick at least one channel'); return; }
      if (unpaid.length === 0) { setErr('Every selected challan is already paid.'); return; }
      payload = { sub_order_ids: ids, channels };
    }

    setSubmitting(true);
    const res = await onSubmit(payload);
    setSubmitting(false);
    if (!res || res.success === false) { setErr(res?.error || 'Bulk action failed'); return; }
    setResult(res.data || null);
  };

  const failedRows = (result?.results || []).filter((r) => r.status !== 'applied');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg">
        <div className="px-5 py-3 border-b flex items-center justify-between">
          <h3 className="font-bold text-gray-800">{titles[action]}</h3>
          <button onClick={() => onClose(Boolean(result))} className="text-gray-400 hover:text-gray-600 text-xl leading-none">&times;</button>
        </div>

        {result ? (
          <div className="p-5">
            <p className="text-sm text-gray-700">
              <span className="font-semibold text-green-700">{result.applied}</span> applied
              {result.skipped > 0 && <> · <span className="font-semibold text-gray-500">{result.skipped}</span> skipped</>}
              {result.failed > 0 && <> · <span className="font-semibold text-red-600">{result.failed}</span> failed</>}
              {' '}of {result.requested}.
            </p>
            {failedRows.length > 0 && (
              <div className="mt-3 max-h-56 overflow-y-auto border rounded-lg divide-y">
                {failedRows.map((r) => (
                  <div key={r.sub_order_id} className="px-3 py-2 text-xs flex justify-between gap-3">
                    <span className="font-medium text-gray-700">{r.challan_number || r.sub_order_id}</span>
                    <span className={r.status === 'failed' ? 'text-red-600' : 'text-gray-500'}>
                      {r.detail || r.status}
                    </span>
                  </div>
                ))}
              </div>
            )}
            <div className="mt-5 flex justify-end">
              <button onClick={() => onClose(true)} className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-semibold">Done</button>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="p-5 space-y-3">
            <p className="text-sm text-gray-600">
              {rows.length} challan{rows.length === 1 ? '' : 's'} selected
              {action === 'markPaid' && (
                <>
                  {' '}· settling <span className="font-semibold text-gray-800">₹ {money(totalOutstanding)}</span> outstanding
                  {rows.length !== unpaid.length && (
                    <span className="text-gray-400"> ({rows.length - unpaid.length} already paid, will be skipped)</span>
                  )}
                </>
              )}
            </p>

            {action === 'markPaid' && (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <label className="block">
                    <span className="text-xs font-semibold text-gray-600">Paid on</span>
                    <input type="date" value={paidOn} onChange={(e) => setPaidOn(e.target.value)}
                      className="mt-1 w-full px-3 py-2 rounded-lg border-2 border-gray-300 text-sm" />
                  </label>
                  <label className="block">
                    <span className="text-xs font-semibold text-gray-600">Mode</span>
                    <select value={mode} onChange={(e) => setMode(e.target.value)}
                      className="mt-1 w-full px-3 py-2 rounded-lg border-2 border-gray-300 text-sm">
                      <option value="bank">Bank</option>
                      <option value="cash">Cash</option>
                      <option value="upi">UPI</option>
                      <option value="cheque">Cheque</option>
                    </select>
                  </label>
                </div>
                <label className="block">
                  <span className="text-xs font-semibold text-gray-600">Reference</span>
                  <input value={reference} onChange={(e) => setReference(e.target.value)}
                    placeholder="Txn / cheque number — applies to every selected challan"
                    className="mt-1 w-full px-3 py-2 rounded-lg border-2 border-gray-300 text-sm" />
                </label>
                <label className="block">
                  <span className="text-xs font-semibold text-gray-600">Note</span>
                  <input value={note} onChange={(e) => setNote(e.target.value)}
                    className="mt-1 w-full px-3 py-2 rounded-lg border-2 border-gray-300 text-sm" />
                </label>
              </>
            )}

            {action === 'dueDate' && (
              <>
                <label className="block">
                  <span className="text-xs font-semibold text-gray-600">New due date</span>
                  <input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)}
                    className="mt-1 w-full px-3 py-2 rounded-lg border-2 border-gray-300 text-sm" />
                </label>
                <label className="block">
                  <span className="text-xs font-semibold text-gray-600">Remark <span className="text-red-500">*</span></span>
                  <input value={remark} onChange={(e) => setRemark(e.target.value)}
                    placeholder="Why the date is changing — recorded against every challan"
                    className="mt-1 w-full px-3 py-2 rounded-lg border-2 border-gray-300 text-sm" />
                </label>
              </>
            )}

            {action === 'reminder' && (
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={email} onChange={(e) => setEmail(e.target.checked)} /> Email
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={whatsapp} onChange={(e) => setWhatsapp(e.target.checked)} /> WhatsApp
                </label>
                <p className="text-xs text-gray-400">
                  Already-paid challans are skipped. Reminders are sent one by one, so a
                  failure for one customer does not stop the rest.
                </p>
              </div>
            )}

            {err && <p className="text-sm text-red-600">{err}</p>}

            <div className="pt-2 flex justify-end gap-2">
              <button type="button" onClick={() => onClose(false)}
                className="px-4 py-2 rounded-lg bg-gray-100 text-gray-700 text-sm font-semibold">Cancel</button>
              <button type="submit" disabled={submitting}
                className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-semibold disabled:opacity-50">
                {submitting ? 'Working…' : titles[action]}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
